/**
 * ðŸ“œ BlueprintLogPanel â€” Excelência Module
 *
 * Modal/panel que exibe o histórico de governança de um blueprint.
 * Mostra timeline vertical com ações: criação, publicação,
    case 'usage_linked': {
      const projectId = details.projectId as string | undefined;
      return projectId ? `Vinculado ao projeto ${projectId.slice(0, 6)}â€¦` : null;
    }
    case 'usage_unlinked': {
      const projectId = details.projectId as string | undefined;
      return projectId ? `Projeto ${projectId.slice(0, 6)}â€¦` : null;
    }
 * versionamento, padrão corporativo, arquivamento, edição, clonagem.
 *
 * Aberto via menu contextual "Ver Histórico" no BlueprintLibraryTable.
 * Memoizado para performance.
 */

import React, { useEffect, useState, useMemo } from 'react';
import {
  X,
  Loader2,
  ScrollText,
  Plus,
  Send,
  GitBranch,
  Award,
  Archive,
  RotateCcw,
  FileEdit,
  Copy,
  ShieldOff,
  Clock,
  Link2,
  Unlink,
} from 'lucide-react';
import type { ProcessBlueprint, ProcessBlueprintLog, BlueprintLogAction } from '../../../types/processBlueprint';
import { listBlueprintLogs } from '../services/blueprintLogService';

// ============================================================================
// HELPERS
// ============================================================================

const ACTION_META: Record<BlueprintLogAction, { label: string; icon: React.ReactNode; color: string }> = {
  created: {
    label: 'Blueprint criado',
    icon: <Plus size={12} />,
    color: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
  },
  published: {
    label: 'Publicado',
    icon: <Send size={12} />,
    color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
  },
  versioned: {
    label: 'Nova versão criada',
    icon: <GitBranch size={12} />,
    color: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
  },
  set_standard: {
    label: 'Definido como padrão',
    icon: <Award size={12} />,
    color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
  },
  removed_standard: {
    label: 'Padrão removido',
    icon: <ShieldOff size={12} />,
    color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
  },
  archived: {
    label: 'Arquivado',
    icon: <Archive size={12} />,
    color: 'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400',
  },
  reactivated: {
    label: 'Reativado',
    icon: <RotateCcw size={12} />,
    color: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
  },
  edited: {
    label: 'Editado',
    icon: <FileEdit size={12} />,
    color: 'bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400',
  },
  cloned: {
    label: 'Clonado',
    icon: <Copy size={12} />,
    color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
  },
  usage_linked: {
    label: 'Vinculado a projeto',
    icon: <Link2 size={12} />,
    color: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
  },
  usage_unlinked: {
    label: 'Desvinculado de projeto',
    icon: <Unlink size={12} />,
    color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
  },
};

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }) + ' ' + d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatUser(id?: string): string {
  if (!id) return 'Sistema';
  return id.length > 8 ? id.slice(0, 8) + 'â€¦' : id;
}

function getDetailsSummary(action: BlueprintLogAction, details: Record<string, unknown>): string | null {
  switch (action) {
    case 'versioned': {
      const prev = details.previousSemver as string | undefined;
      const next = details.newSemver as string | undefined;
      if (prev && next) return `v${prev} → v${next}`;
      return null;
    }
    case 'edited': {
      const summary = details.changeSummary as string | undefined;
      return summary ?? null;
    }
    case 'cloned': {
      const name = details.name as string | undefined;
      return name ? `"${name}"` : null;
    }
    case 'set_standard':
    case 'removed_standard': {
      const cat = details.category as string | undefined;
      return cat ? `Categoria: ${cat}` : null;
    }
    case 'archived':
    case 'published':
    case 'reactivated': {
      const prev = details.previousStatus as string | undefined;
      return prev ? `Status anterior: ${prev}` : null;
    }
    case 'created': {
      const src = details.sourceType as string | undefined;
      return src ? `Origem: ${src}` : null;
    }
    default:
      return null;
  }
}

// ============================================================================
// PROPS
// ============================================================================

interface BlueprintLogPanelProps {
  /** Blueprint selecionado. Se null, o painel não renderiza */
  blueprint: ProcessBlueprint | null;
  /** Callback para fechar o painel */
  onClose: () => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const BlueprintLogPanel: React.FC<BlueprintLogPanelProps> = React.memo(
  ({ blueprint, onClose }) => {
    const [logs, setLogs] = useState<ProcessBlueprintLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      if (!blueprint) return;

      let cancelled = false;
      setLoading(true);
      setError(null);

      listBlueprintLogs(blueprint.id)
        .then(data => {
          if (!cancelled) setLogs(data);
        })
        .catch(err => {
          if (!cancelled) setError(err instanceof Error ? err.message : 'Erro ao carregar histórico');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });

      return () => { cancelled = true; };
    }, [blueprint]);

    // Don't render if no blueprint selected
    if (!blueprint) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="w-full max-w-lg bg-white dark:bg-[var(--bg-surface)] rounded shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden max-h-[80vh] flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-slate-800 shrink-0">
            <div className="flex items-center gap-2">
              <ScrollText size={18} className="text-purple-500" />
              <h2 className="text-sm font-bold text-gray-900 dark:text-slate-100">
                Histórico de Governança
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors"
            >
              <X size={16} className="text-gray-500" />
            </button>
          </div>

          {/* Blueprint info */}
          <div className="px-5 py-3 bg-gray-50 dark:bg-slate-950 border-b border-gray-200 dark:border-slate-800 shrink-0">
            <p className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
              {blueprint.name}
            </p>
            <p className="text-[11px] text-gray-500 dark:text-slate-500">
              v{blueprint.versionSemver} Â· {blueprint.stages.length} etapas Â· {blueprint.status}
            </p>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={16} className="animate-spin text-purple-500" />
                <span className="text-xs text-gray-500 ml-2">Carregando histórico...</span>
              </div>
            )}

            {error && (
              <div className="text-center py-8">
                <p className="text-sm text-red-500">{error}</p>
              </div>
            )}

            {!loading && !error && logs.length === 0 && (
              <div className="text-center py-8">
                <Clock size={28} className="text-gray-300 dark:text-slate-700 mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  Nenhum registro de governança encontrado.
                </p>
                <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">
                  Ações futuras serão registradas automaticamente.
                </p>
              </div>
            )}

            {!loading && !error && logs.length > 0 && (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-[15px] top-2 bottom-2 w-px bg-gray-200 dark:bg-slate-800" />

                <div className="space-y-4">
                  {logs.map(log => {
                    const meta = ACTION_META[log.action] ?? {
                      label: String(log.action ?? 'Ação desconhecida'),
                      icon: <Clock size={12} />,
                      color: 'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400',
                    };
                    const detail = getDetailsSummary(log.action, log.details);

                    return (
                      <div key={log.id} className="flex items-start gap-3 relative">
                        {/* Icon circle */}
                        <div className={`w-[30px] h-[30px] rounded-full flex items-center justify-center shrink-0 z-10 ${meta.color}`}>
                          {meta.icon}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0 pt-0.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-medium text-gray-900 dark:text-slate-100">
                              {meta.label}
                            </span>
                            <span className="text-[10px] text-gray-400 dark:text-slate-500 whitespace-nowrap">
                              {formatDateTime(log.createdAt)}
                            </span>
                          </div>

                          {detail && (
                            <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-0.5 truncate">
                              {detail}
                            </p>
                          )}

                          <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5">
                            por {formatUser(log.performedBy)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-gray-200 dark:border-slate-800 flex items-center justify-between shrink-0">
            <span className="text-[10px] text-gray-400 dark:text-slate-500">
              {logs.length} registro{logs.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  },
);

BlueprintLogPanel.displayName = 'BlueprintLogPanel';
